/// <amd-module name="VSSUI/Components/TeachingBubble/CustomTeachingBubble" />
import * as React from "react";

import { ITeachingBubble, ICustomTeachingBubbleProps, TeachingBubbleCornerPlacement } from "./TeachingBubble.Props";

import { Callout, ContentJustification, ContentLocation, ContentOrientation } from "VSSUI/Callout";
import { FocusZoneKeyStroke } from "VSSUI/FocusZone";
import { css, KeyCode } from "VSSUI/Util";
import { ScreenSize } from "VSS/Core/Util/Screen";
import { Location, IOrigin } from "VSSUI//Utilities/Position";
import { ScreenSizeObserver } from "VSSUI/Utilities/ScreenSize";

/**
 * Used to manage animations and content during animations.
 */
export interface ICustomTeachingBubbleState {
    anchorElement: HTMLElement;

    anchorOrigin: IOrigin;

    children?: React.ReactNode;

    cornerPlacement?: TeachingBubbleCornerPlacement;

    fadingOut?: boolean;

    newPosition?: boolean;
}

export class CustomTeachingBubble extends React.Component<ICustomTeachingBubbleProps, ICustomTeachingBubbleState> implements ITeachingBubble {
    public static getDerivedStateFromProps(props: Readonly<ICustomTeachingBubbleProps>, state: Readonly<ICustomTeachingBubbleState>) {
        if (
            state.anchorElement !== props.anchorElement ||
            state.anchorOrigin.horizontal !== props.anchorOrigin.horizontal ||
            state.anchorOrigin.vertical !== props.anchorOrigin.vertical
        ) {
            return { ...state, newPosition: true };
        } else {
            return { children: (props as any).children, cornerPlacement: props.cornerPlacement };
        }
    }

    constructor(props: ICustomTeachingBubbleProps) {
        super(props);

        this.state = {
            anchorElement: props.anchorElement,
            anchorOrigin: props.anchorOrigin,
            children: (props as any).children,
            cornerPlacement: this.props.cornerPlacement
        };
    }

    public render() {
        const {
            anchorElement,
            anchorOrigin,
            children,
            cornerPlacement = TeachingBubbleCornerPlacement.vertical,
            fadingOut,
            newPosition
        } = this.state;

        const calloutOrigin = this.getCalloutOrigin(anchorOrigin, cornerPlacement);

        return (
            <ScreenSizeObserver>
                {(screenSizeProps: { screenSize: ScreenSize }) => {
                    const fullScreen = screenSizeProps.screenSize === ScreenSize.xsmall;
                    let maxHeight;
                    if (!fullScreen) {
                        if (anchorOrigin.vertical === Location.end) {
                            maxHeight = window.innerHeight - anchorElement.getBoundingClientRect().bottom;
                        } else if (anchorOrigin.vertical === Location.start) {
                            maxHeight = anchorElement.getBoundingClientRect().top;
                        } else {
                            maxHeight = window.innerHeight;
                        }
                    }

                    return (
                        <Callout
                            anchorElement={fullScreen ? undefined : anchorElement}
                            anchorPoint={fullScreen ? {x: 0, y: 0} : undefined}
                            anchorOrigin={anchorOrigin}
                            ariaDescribedBy={this.props.ariaDescribedBy}
                            ariaLabelledBy={this.props.ariaLabeledBy}
                            blurDismiss={this.props.textOnly}
                            calloutOrigin={calloutOrigin}
                            className={css(fullScreen && "absolute-fill")}
                            contentClassName={css(
                                "bolt-bubble-callout",
                                (fadingOut || newPosition) && "fade-out",
                                this.getBeakClassName(anchorOrigin, calloutOrigin),
                                fullScreen ? "bolt-bubble-fullscreen absolute-fill flex-grow scroll-auto" : "relative"
                            )}
                            contentShadow={true}
                            escDismiss={true}
                            fixedLayout={true}
                            focuszoneProps={{
                                circularNavigation: true,
                                defaultActiveElement: this.props.defaultActiveElement || ".bolt-bubble-focusable-element",
                                focusOnMount: true,
                                handleTabKey: true,
                                includeDefaults: true,
                                postprocessKeyStroke: function (event: React.KeyboardEvent<HTMLElement>) {
                                    // We want to prevent moving outside the bubble if there are no focusable elements in the bubble.
                                    event.which === KeyCode.tab && event.preventDefault();
                                    return FocusZoneKeyStroke.IgnoreParents;
                                }
                            }}
                            onAnimationEnd={this.onAnimationEnd}
                            onDismiss={this.dismiss}
                            updateLayout={true}
                            viewportChangeDismiss={false}
                        >
                            <div aria-live="polite" aria-relevant="text" className="bolt-bubble-container">
                                {!fullScreen && <div className="bolt-bubble-beak depth-8" />}
                                <div className="bolt-bubble-content relative scroll-auto" style={{ maxHeight: maxHeight }}>
                                    <div className="bolt-bubble-focusable-element no-outline" tabIndex={-1} />
                                    {children}
                                </div>
                            </div>
                        </Callout>
                    );
                }}
            </ScreenSizeObserver>
        );
    }

    public dismiss = () => {
        this.setState({ fadingOut: true });
    };

    private onAnimationEnd = () => {
        if (this.state.fadingOut) {
            this.props.onDismiss && this.props.onDismiss();
        } else if (this.state.newPosition) {
            this.setState(
                {
                    anchorElement: this.props.anchorElement,
                    anchorOrigin: this.props.anchorOrigin,
                    children: (this.props as any).children,
                    cornerPlacement: this.props.cornerPlacement,
                    newPosition: false
                },
                this.props.onLocationChange
            );
        }
    };

    private getCalloutOrigin = (anchorOrigin: IOrigin, cornerPlacement: TeachingBubbleCornerPlacement) => {
        if (anchorOrigin.horizontal === Location.center) {
            cornerPlacement = TeachingBubbleCornerPlacement.vertical;
        } else if (anchorOrigin.vertical === Location.center) {
            cornerPlacement = TeachingBubbleCornerPlacement.horizontal;
        }

        if (cornerPlacement === TeachingBubbleCornerPlacement.horizontal) {
            if (anchorOrigin.horizontal === Location.start) {
                return { horizontal: Location.end, vertical: anchorOrigin.vertical };
            } else if (anchorOrigin.horizontal === Location.end) {
                return { horizontal: Location.start, vertical: anchorOrigin.vertical };
            } else {
                return { horizontal: Location.center, vertical: anchorOrigin.vertical };
            }
        } else {
            if (anchorOrigin.vertical === Location.start) {
                return { horizontal: anchorOrigin.horizontal, vertical: Location.end };
            } else if (anchorOrigin.vertical === Location.end) {
                return { horizontal: anchorOrigin.horizontal, vertical: Location.start };
            } else {
                return { horizontal: anchorOrigin.horizontal, vertical: Location.center };
            }
        }
    };

    private getBeakClassName = (anchorOrigin: IOrigin, calloutOrigin: IOrigin) => {
        let classNameHorizontal, classNameVertical;

        if (calloutOrigin.horizontal === Location.start) {
            if (anchorOrigin.horizontal === Location.end) {
                classNameHorizontal = "left-primary";
            } else {
                classNameHorizontal = "left-secondary";
            }
        } else if (calloutOrigin.horizontal === Location.end) {
            if (anchorOrigin.horizontal === Location.start) {
                classNameHorizontal = "right-primary";
            } else {
                classNameHorizontal = "right-secondary";
            }
        } else {
            classNameHorizontal = "center-h";
        }

        if (calloutOrigin.vertical === Location.start) {
            if (anchorOrigin.vertical === Location.end) {
                classNameVertical = "top-primary";
            } else {
                classNameVertical = "top-secondary";
            }
        } else if (calloutOrigin.vertical === Location.end) {
            if (anchorOrigin.vertical === Location.start) {
                classNameVertical = "bottom-primary";
            } else {
                classNameVertical = "bottom-secondary";
            }
        } else {
            classNameVertical = "center-v";
        }

        return css(classNameHorizontal, classNameVertical);
    };
}
